import React, { useEffect } from 'react';
import styled from 'styled-components';
import { Pager, PagerProps } from '@progress/kendo-react-data-tools';
import { useSelector } from 'react-redux';
import { selectThemeKey } from 'styles/theme/slice/selectors';
import { removeTooltipInGridPagination } from 'utils/common';

type pagerTypes = {
  props: PagerProps;
  totalCount?: number;
  onPageInputChange?: React.ChangeEventHandler<HTMLInputElement>;
  onPageSubmit?: (skipValue: number) => void;
  gridPageNum?: number;

  setGridPageNum?: React.Dispatch<React.SetStateAction<number | undefined>>;
  setSkip?: React.Dispatch<React.SetStateAction<number>>;
};

const CustomPagination = ({
  props,
  totalCount,
  onPageInputChange,
  onPageSubmit,
  gridPageNum,
  setGridPageNum,
  setSkip,
}: pagerTypes) => {
  const { skip, take, total } = props;
  const start = skip;
  const end = Math.min(skip + take, total);

  const resultText = `Showing ${start}-${end} of ${total} items`;
  const ThemeKey = useSelector(selectThemeKey);
  const [gridPageNumInner, setGridPageNumInner] = React.useState<
    number | undefined
  >(skip / take + 1);

  const onChangePageInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const page = Number((e.target as HTMLInputElement).value);

    if (page >= 1 && page <= Math.ceil(total / take)) {
      setGridPageNumInner?.(page);
    } else if (page > Math.ceil(total / take)) {
      e.stopPropagation();
      e.preventDefault();
      return;
    } else {
      setGridPageNumInner?.(undefined);
    }
  };

  const onSubmitBtnClick = (page: number) => {
    const skipValue = (page - 1) * take;
    onPageSubmit?.(skipValue);
  };

  useEffect(() => {
    //remove unwanted tooltip in prev/next buttons
    removeTooltipInGridPagination();
  }, []);

  return (
    <CustomPaginationWrapper
      className={`${ThemeKey ?? ''} custom-pagination-wrapper`}
    >
      <PagerWrapper className="pager-wrapper">
        <Pager
          {...props}
          className={`${totalCount === 0 ? 'no-entries' : ''} ${ThemeKey}`}
        />
        <InfoText>{resultText}</InfoText>
        <PageControl className="page-controls">
          <span>Page</span>
          <PageInput
            type={'number'}
            min={1}
            max={Math.ceil(total / take)}
            value={gridPageNumInner}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                e.stopPropagation();
                e.preventDefault();
                onSubmitBtnClick(gridPageNumInner!);
              }
            }}
            onChange={onChangePageInput}
          />
          <GoButton
            className="submit-go-button"
            type="button"
            onClick={() => onSubmitBtnClick(gridPageNumInner!)}
          >
            Go
          </GoButton>
        </PageControl>
      </PagerWrapper>
    </CustomPaginationWrapper>
  );
};
export default CustomPagination;
const CustomPaginationWrapper = styled.div`
  width: 100%;
  padding: 26px 17px 30px 17px;
  display: inline-block !important;
  .k-pager.k-widget {
    font-family: 'open sans';
    font-weight: 400;
    padding: 0;
    border: none !important;
    border-radius: 0px;
    max-height: 40px;
    background: transparent;
    font-size: 14px;
    border: none;
    &.no-entries {
      visibility: hidden;
    }
    .k-pager-numbers-wrap {
      display: none;
    }
    .k-pager-first,
    .k-pager-last {
      display: none;
    }
    .k-link {
      margin: 0 0 0 0;
      :not(.k-disabled) {
        &:hover {
        }
      }
      &.next-page-btn {
        border: 1px solid ${p => p.theme.paginationButtonBorder};
        padding: 0 !important;
        display: flex !important;
        align-items: center !important;
        justify-content: center !important;
        background-color: transparent !important;
        border-radius: 4px !important;
        box-shadow: none;
        margin: 0 0 0 6px;
        height: 36px;
        width: 40px;
        justify-content: end;
        position: relative;
        text-decoration: none;
        color: ${p => p.theme.tablePaginationColor} !important;
        &:hover {
        }
        span {
          ::before {
            content: '';
            width: 15px;
            height: 15px;
            filter: 0;
            left: 4px;
            position: relative;
          }
          ::after {
            content: url('data:image/svg+xml,%3Csvg%20width%3D%226%22%20height%3D%229%22%20viewBox%3D%220%200%206%209%22%20fill%3D%22none%22%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%3E%3Cpath%20fill-rule%3D%22evenodd%22%20clip-rule%3D%22evenodd%22%20d%3D%22M0.767477%208.04536C0.5378%207.80649%200.545248%207.42667%200.784111%207.19699L3.93431%204.22949L0.784111%201.26199C0.545248%201.03231%200.5378%200.652489%200.767476%200.413626C0.997152%200.174764%201.37698%200.167315%201.61584%200.396992L5.21584%203.79699C5.33349%203.91011%205.39998%204.06628%205.39998%204.22949C5.39998%204.3927%205.33349%204.54887%205.21584%204.66199L1.61584%208.06199C1.37698%208.29167%200.997153%208.28422%200.767477%208.04536Z%22%20fill%3D%22%23626262%22/%3E%3C/svg%3E');
            display: inline-block;
            width: 6px;
            height: 15px;
            font-family: 'open sans';
            font-weight: 600;
            font-size: 14px;
            display: inline-block;
            position: absolute;
          }
        }
      }
      &.k-disabled {
        &.next-page-btn {
          span {
            ::before {
              opacity: 0.5;
            }
            ::after {
              opacity: 0.5;
            }
          }
        }
        &.prev-page-btn {
          span {
            ::before {
              opacity: 0.5;
            }
            ::after {
              opacity: 0.5;
            }
          }
        }
      }
      &.prev-page-btn {
        border: 1px solid ${p => p.theme.paginationButtonBorder};
        padding: 0 !important;
        display: flex !important;
        align-items: center !important;
        justify-content: center !important;
        background-color: transparent !important;
        border-radius: 4px !important;
        box-shadow: none;
        margin: 0 5px 0 0;
        height: 36px;
        width: 40px;
        justify-content: flex-start;
        position: relative;
        text-decoration: none;
        color: ${p => p.theme.tablePaginationColor} !important;
        &:hover {
        }
        span {
          ::before {
            content: '';
            left: 4px;
            position: relative;
            background-size: contain;
            width: 15px;
            height: 15px;
            filter: 0;
          }
          ::after {
            content: url('data:image/svg+xml,%3Csvg%20width%3D%226%22%20height%3D%229%22%20viewBox%3D%220%200%206%209%22%20fill%3D%22none%22%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%3E%3Cpath%20fill-rule%3D%22evenodd%22%20clip-rule%3D%22evenodd%22%20d%3D%22M5.23252%200.413628C5.4622%200.652491%205.45475%201.03232%205.21589%201.26199L2.06569%204.22949L5.21589%207.19699C5.45475%207.42667%205.4622%207.8065%205.23252%208.04536C5.00285%208.28422%204.62302%208.29167%204.38416%208.06199L0.784158%204.66199C0.66651%204.54887%200.600023%204.3927%200.600023%204.22949C0.600023%204.06628%200.66651%203.91012%200.784158%203.79699L4.38416%200.396993C4.62302%200.167317%205.00285%200.174765%205.23252%200.413628Z%22%20fill%3D%22%23626262%22/%3E%3C/svg%3E');
            display: inline-block;
            width: 6px;
            height: 15px;
            font-family: 'open sans';
            font-weight: 600;
            font-size: 14px;
            display: inline-block;
            position: absolute;
          }
        }
      }
    }

    .k-pager-info {
      justify-content: end;
    }
  }
`;
const PagerWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  padding: 10px 0;
  margin: 0;
  .pagerInfo {
    align-items: center;
    display: flex;
    font-size: 10px;
    font-family: 'lato';
    font-weight: 600;
    margin-right: 20px;
  }
`;

const Button = styled.button`
  border: 1px solid ${p => p.theme.paginationGoButtonBorder};
  background-color: ${p => p.theme.paginationGoButtonBg};
  padding: 5px 10px;
  cursor: pointer;
  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

const InfoText = styled.span`
  font-family: 'open sans';
  font-weight: 400;
  font-size: 12px;
  color: ${p => p.theme.paginationInfoText};
`;

const PageControl = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: ${p => p.theme.paginationInfoText};
  font-family: 'open sans';
  font-weight: 400;
  font-size: 12px;
`;

const PageInput = styled.input`
  min-width: 40px;
  max-width: 70px !important;
  width: fit-content;
  height: 36px;
  padding: 5px;
  border: 1px solid ${p => p.theme.paginationGoButtonBorder};
  border-radius: 4px;
  text-align: center;
  &:focus-visible {
    outline: 0 !important;
  }
  &:focus {
    outline: 0 !important;
  }
`;

const GoButton = styled(Button)`
  padding: 5px 10px;
  font-family: 'open sans';
  font-weight: 700;
  font-size: 12px;
  background: transparent;
  border: none !important;
  box-shadow: none !important;
  outline: none !important;
  &:hover {
    background: ${p => p.theme.paginationGoButtonBg};
    border: none !important;
    box-shadow: none !important;
    outline: none !important;
  }
`;
